import GoalProgress from '@features/reports/components/goals/management/GoalProgress'
import { colors } from '@lmc/common/constants'
import { ChevronRightIcon } from '@lmc/design-system'
import type { GoalStatus } from '@lmc/ts-common'
import { Tag } from '@lmc/ui-kit'
import { useAbilityAccess } from 'components/AccessControl'
import { getRouteWithParams } from 'components/routes/utils'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { getTagColorByStatus } from 'screens/Residents/screens/ResidentsGoalsScreen/components/GoalsList/GoalItem'
import styled from 'styled-components'
import { NAMESPACE } from '../../../../locale'
import { goalReportsConstants } from '../../../../locale/goalReports'
import { logsConstants } from '../../../../locale/logs'
import { SectionTitle } from './LogViewFooter'

export interface LinkedGoal {
  id: string
  title: string
  progress?: number
  status?: string
}

interface LogLinkedGoalsProps {
  goals: LinkedGoal[]
  residentId?: string
  testId?: string
}

const LogLinkedGoals: FC<LogLinkedGoalsProps> = ({
  goals,
  residentId,
  testId = 'log-linked-goals'
}) => {
  const { t } = useTranslation(NAMESPACE.LOGS)
  const { t: tGoals } = useTranslation(NAMESPACE.GOAL_REPORTS)
  const navigate = useNavigate()
  const canViewGoals = useAbilityAccess([{ i: 'view', this: 'Goal' }])

  if (!goals.length) return null

  const openGoal = (goalID: string) =>
    navigate(
      getRouteWithParams('/residents/:id/goals/:goalID', {
        goalID,
        id: residentId
      })
    )

  return (
    <Container data-testid={testId}>
      <SectionTitle>{t(logsConstants.LOG_VIEW_LINKED_GOALS)}</SectionTitle>
      {goals.map((goal) => {
        const status = goal.status as GoalStatus
        return (
          <GoalRow
            key={goal.id}
            type="button"
            disabled={!canViewGoals}
            onClick={() => openGoal(goal.id)}
            data-testid={`${testId}-${goal.id}`}
          >
            <GoalProgress percentage={goal.progress ?? 0} status={status} />
            <GoalText>
              <GoalTitle>{goal.title}</GoalTitle>
              {status && goalReportsConstants[status] && (
                <StatusTag color={getTagColorByStatus(status)}>
                  {tGoals(goalReportsConstants[status])}
                </StatusTag>
              )}
            </GoalText>
            {canViewGoals && <ChevronRightIcon size={24} />}
          </GoalRow>
        )
      })}
    </Container>
  )
}

export default LogLinkedGoals

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const GoalRow = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  color: ${colors.black};
  cursor: pointer;
  display: flex;
  gap: 12px;
  min-height: 64px;
  padding: 12px 0;
  text-align: left;
  width: 100%;

  &:disabled {
    cursor: default;
  }

  & + & {
    border-top: 1px solid ${colors.lightgrey};
  }
`

const GoalText = styled.span`
  align-items: flex-start;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`

const GoalTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
`

const StatusTag = styled(Tag)`
  margin: 0;
`
